import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown, Copy } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const LanguageSwitcher = ({ className = '' }) => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const [showUrlTooltip, setShowUrlTooltip] = useState('');

  // 支持中文、英文、韩文
  const languages = [
    { code: 'zh-CN', name: '中文', displayName: t('header.languages.chinese') },
    { code: 'en-US', name: 'English', displayName: t('header.languages.english') },
    { code: 'ko-KR', name: '한국어', displayName: t('header.languages.korean') }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng).then(() => {
      // 强制重新渲染所有使用翻译的组件
      window.dispatchEvent(new Event('languageChanged'));
      // 延迟关闭下拉菜单，确保语言切换完成
      setTimeout(() => {
        setIsOpen(false);
      }, 100);
    });
  };

  // 生成带语言参数的URL
  const generateLanguageUrl = (langCode) => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('lang', langCode);
    return currentUrl.toString();
  };

  // 复制语言URL到剪贴板
  const copyLanguageUrl = async (langCode, event) => {
    event.stopPropagation();
    const url = generateLanguageUrl(langCode);
    
    try {
      await navigator.clipboard.writeText(url);
      setShowUrlTooltip(langCode);
      setTimeout(() => setShowUrlTooltip(''), 2000);
    } catch (err) {
      console.error('复制失败:', err);
    }
  };

  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // 判断是否在移动端样式中
  const isMobile = className.includes('mobile');
  // 判断是否在侧边栏中
  const isSidebar = className.includes('sidebar-language-switcher');
  // 判断侧边栏是否收缩
  const isSidebarCollapsed = className.includes('collapsed');
  // 判断是否是内联样式（展开状态下的右侧显示）
  const isSidebarInline = className.includes('sidebar-inline-language-switcher');

  return (
    <div className={`relative language-switcher ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-2 text-sm transition-all duration-300 ${
          isSidebarInline
            ? 'text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-50'
            : isSidebar
              ? isSidebarCollapsed
                ? 'text-gray-600 hover:text-gray-800 p-2 rounded-lg hover:bg-gray-100'
                : 'text-gray-600 hover:text-gray-800 px-3 py-2 rounded-lg hover:bg-gray-100'
              : isMobile 
                ? 'text-white/80 hover:text-white px-3 py-2 rounded-lg hover:bg-white/10' 
                : 'text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10'
        }`}
        title={isSidebarCollapsed ? t('common.switchLanguage') : ''}
      >
        {!isSidebarInline && <Globe className="w-4 h-4" />}
        {(!isSidebarCollapsed || isSidebarInline) && <span>{currentLanguage.name}</span>}
        {(!isSidebarCollapsed || isSidebarInline) && <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />}
      </button>
      
      {isOpen && (
        <div className={`absolute ${isSidebarInline ? 'right-0' : isSidebar ? 'left-0' : 'right-0'} mt-2 w-32 rounded-lg shadow-lg py-2 z-50 overflow-hidden ${
          isSidebar || isSidebarInline
            ? 'bg-white border border-gray-200'
            : isMobile
              ? 'bg-white/10 backdrop-blur-sm border border-white/20'
              : 'bg-white border border-gray-200'
        }`}>
          {languages.map((lang) => (
            <div key={lang.code} className="relative group">
              <div className={`flex items-center justify-between w-full ${
                i18n.language === lang.code 
                  ? isSidebar || isSidebarInline
                    ? 'bg-orange-100 text-orange-600'
                    : isMobile
                      ? 'bg-white/20 text-white' 
                      : 'bg-orange-100 text-orange-600'
                  : isSidebar || isSidebarInline
                    ? 'text-gray-700 hover:bg-gray-100'
                    : isMobile
                      ? 'text-white/80 hover:text-white hover:bg-white/10'
                      : 'text-gray-700 hover:bg-gray-100'
              } transition-all duration-300`}>
                <button
                  onClick={() => changeLanguage(lang.code)}
                  className={`flex-1 text-left px-3 py-2 text-sm ${
                    i18n.language === lang.code ? 'font-medium' : ''
                  }`}
                >
                  {lang.name}
                </button>
                
                {/* 复制URL按钮 */}
                <button
                  onClick={(e) => copyLanguageUrl(lang.code, e)}
                  className={`p-1.5 mr-1 rounded opacity-0 group-hover:opacity-100 transition-all duration-200 ${
                    isSidebar || isSidebarInline
                      ? 'hover:bg-gray-200'
                      : isMobile
                        ? 'hover:bg-white/20'
                        : 'hover:bg-gray-200'
                  }`}
                  title={t('common.copyLanguageUrl', '复制语言链接')}
                >
                  <Copy className="w-3 h-3" />
                </button>
                
                {/* 复制成功提示 */}
                {showUrlTooltip === lang.code && (
                  <div className="absolute right-0 top-0 -mt-8 px-2 py-1 bg-green-500 text-white text-xs rounded whitespace-nowrap z-50">
                    {t('common.copied', '已复制')}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher; 